import { useState, useRef, useMemo } from "react";

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import { Button } from "@mui/material";

import { useAsync } from "../../../hooks/useAsync";

import useEquipmentService from "../../../services/equipmentService";

import BasicDialog from "../../base/BasicDialog";

import EquipmentTable from "../../table/EquipmentTable/EquipmentTable";
import { EQUIPMENT_SCHEME_TABLE_CONFIG } from "./TableConfig";

import EquipmentSchemeDialog from "../EquipmentSchemeDialog/EquipmentSchemeDialog";
import EquipmentUsageRecalculateDialog from "../EquipmentUsageRecalculateDialog/EquipmentUsageRecalculateDialog";
import ElectricityDistributionRelalculateDialog from "../ElectricityDistributionRecalculateDialog/ElectricityDistributionRecalculationDialog";

import useDialogWithId from "../../../hooks/useDialogWithId";
import LoaderWrapper from "../../wrapper/LoaderWrapper";

import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import {
  centerVericalAlignIconStyle,
  overflowButtonStyle,
} from "../../../helpers/styles";

import {
  DIALOG_CREATE_MODE,
  DIALOG_EDIT_MODE,
} from "../../../helpers/constants";

const EquipmentSchemeListDialog = (props) => {
  const { t } = useTranslation();

  const [existChanges, setExistChanges] = useState(false);

  const [
    openEquipmentSchemeDialog,
    equipmentSchemeId,
    onOpenEquipmentSchemeDialog,
    onCloseEquipmentSchemeDialog,
  ] = useDialogWithId();

  const [
    openEquipmentSchemeUsageDialog,
    equipmentSchemeUsageId,
    onOpenEquipmentSchemeUsageDialog,
    onCloseEquipmentSchemeUsageDialog,
  ] = useDialogWithId();

  const [
    openEquipmentSchemeEdDialog,
    equipmentSchemeEdId,
    onOpenEquipmentSchemeEdDialog,
    onCloseEquipmentSchemeEdDialog,
  ] = useDialogWithId();

  const preparedTableConfig = useMemo(
    () =>
      props.readOnly
        ? EQUIPMENT_SCHEME_TABLE_CONFIG.filter(
            (config) =>
              !["recalculate_usage", "recalculate_ed", "edit"].includes(
                config.name
              )
          )
        : props.isForElectricalEnergy
        ? EQUIPMENT_SCHEME_TABLE_CONFIG
        : EQUIPMENT_SCHEME_TABLE_CONFIG.filter(
            (config) => config.name !== "recalculate_ed"
          ),
    [props.isForElectricalEnergy, props.readOnly]
  );

  const equipmentSchemeDialogModeRef = useRef();
  const handleOpenequipmentSchemeDialogInCreateMode = () => {
    equipmentSchemeDialogModeRef.current = DIALOG_CREATE_MODE;
    onOpenEquipmentSchemeDialog(undefined);
  };

  const handleOpenEquipmentSchemeInEditMode = (equipmentSchemeId) => {
    equipmentSchemeDialogModeRef.current = DIALOG_EDIT_MODE;
    onOpenEquipmentSchemeDialog(equipmentSchemeId);
  };

  const { getEquipmentSchemeListData } = useEquipmentService();

  const equipmentSchemeList = useAsync(
    () => getEquipmentSchemeListData({ equipment: props.equipmentId }),
    []
  );

  const onSumbitCallbackEquipmentSchemeDialog = (existChanges) => {
    if (existChanges) {
      setExistChanges(true);
      equipmentSchemeList.refetch();
    }
  };

  const handleClose = () => {
    if (existChanges && props.onSubmit) {
      props.onSubmit();
    }
    props.onClose();
  };

  const getDialogContent = () => {
    if (equipmentSchemeList.loading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={1}
      >
        {!props.readOnly && (
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="contained"
              size="small"
              startIcon={
                <ControlPointOutlinedIcon sx={centerVericalAlignIconStyle} />
              }
              onClick={handleOpenequipmentSchemeDialogInCreateMode}
              sx={overflowButtonStyle}
            >
              {t("dialog.equipment.add_scheme")}
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <EquipmentTable
            data={equipmentSchemeList.value}
            showCheckbox={false}
            showCleanFilterIcon={false}
            countRecords={equipmentSchemeList.value.length}
            style={{ maxHeight: "75vh" }}
            tableConfig={preparedTableConfig}
            hiddenColumns={[]}
            columnsOrders={[]}
            showExportToFileButton={false}
            withPagination={false}
            showFilters={false}
            onClickMultipleLocationAlert={handleOpenEquipmentSchemeInEditMode}
            onClickAssignData={onOpenEquipmentSchemeUsageDialog}
            onClickThridButton={onOpenEquipmentSchemeEdDialog}
                  isLoading={equipmentSchemeList.loading}
                  showTableLoader={true}
          />
        </Grid>

        {openEquipmentSchemeDialog && (
          <EquipmentSchemeDialog
            open={openEquipmentSchemeDialog}
            onClose={onCloseEquipmentSchemeDialog}
            equipmentId={props.equipmentId}
            equipmentSchemeId={equipmentSchemeId}
            onSubmitCallback={onSumbitCallbackEquipmentSchemeDialog}
            dialogMode={equipmentSchemeDialogModeRef.current}
          />
        )}
        {openEquipmentSchemeUsageDialog && (
          <EquipmentUsageRecalculateDialog
            equipmentId={props.equipmentId}
            onClose={onCloseEquipmentSchemeUsageDialog}
            open={openEquipmentSchemeUsageDialog}
            equipmentSchemeId={equipmentSchemeUsageId}
          />
        )}
        {openEquipmentSchemeEdDialog && (
          <ElectricityDistributionRelalculateDialog
            equipmentId={props.equipmentId}
            onClose={onCloseEquipmentSchemeEdDialog}
            open={openEquipmentSchemeEdDialog}
            equipmentSchemeId={equipmentSchemeEdId}
          />
        )}
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={handleClose}
      titleAlign="center"
      title={t("dialog.equipment.equipment_schemes")}
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentSchemeListDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  equipmentId: PropTypes.string,
};

EquipmentSchemeListDialog.defaultProps = {};

export default EquipmentSchemeListDialog;
